import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAppState } from '../context/AppStateContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    XMarkIcon,
    CheckCircleIcon,
    MagnifyingGlassIcon,
    AcademicCapIcon,
    ClockIcon,
    UserIcon,
    PencilSquareIcon,
    ArrowDownTrayIcon as DownloadIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PlusIcon,
    ChatBubbleBottomCenterTextIcon,
    SparklesIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';
import BlueprintCreator from './teacher/BlueprintCreator';
import { getScoreFromLevel } from '../utils/cbcUtils';

const GradeForm = ({ assignmentId, onClose, isClub = false, initialStudentId = null, isPage = false }) => {
    const navigate = useNavigate();
    const { get, patch, post } = useApi();
    const { showToast, refresh } = useAppState();

    // Interaction State
    const [currentIndex, setCurrentIndex] = useState(0);
    const [grades, setGrades] = useState({});
    const [rawMarks, setRawMarks] = useState({});
    const [teacherComments, setTeacherComments] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isForgingTemplate, setIsForgingTemplate] = useState(false);

    // Status State
    const [submitting, setSubmitting] = useState(false);

    const queryClient = useQueryClient();

    // 1. Fetch Assignment
    const { data: assignment, isLoading: loadingAssignment } = useQuery({
        queryKey: ['assignment', assignmentId, isClub],
        queryFn: () => get(isClub ? `/api/events/club-assignments/${assignmentId}/` : `/api/assignments/${assignmentId}/`),
        staleTime: 2 * 60 * 1000,
    });

    const laId = assignment?.learning_area || assignment?.course;

    // 2. Fetch Submissions
    const { data: submissions = [], isLoading: loadingSubmissions } = useQuery({
        queryKey: ['assignment-submissions', assignmentId, isClub],
        queryFn: () => get(isClub ? `/api/events/club-assignments/${assignmentId}/submissions/` : `/api/assignments/${assignmentId}/submissions/`),
        staleTime: 2 * 60 * 1000,
    });

    // 3. Fetch Templates
    const templateParams = isClub && assignment?.club
        ? `club_id=${assignment.club}`
        : `learning_area_id=${laId}`;

    const { data: templates = [], isLoading: loadingTemplates } = useQuery({
        queryKey: ['learning-area-templates', isClub, assignment?.club, laId],
        queryFn: () => get(`/api/cbc/templates/by_learning_area/?${templateParams}`),
        enabled: isClub ? !!assignment?.club : !!laId,
        staleTime: 2 * 60 * 1000,
    });

    // 4. Fetch Students
    const studentEndpoint = isClub 
        ? `/api/events/clubs/${assignment?.club}/roster/`
        : (assignment?.course
            ? `/api/courses/${assignment.course}/students/`
            : `/api/cbc/learning-areas/${assignment?.learning_area}/students/?exclude_assessed_assignment=${assignmentId}`);

    const { data: students = [], isLoading: loadingStudents } = useQuery({
        queryKey: ['assignment-students', assignmentId, studentEndpoint],
        queryFn: () => get(studentEndpoint),
        enabled: !!assignment,
        staleTime: 2 * 60 * 1000,
    });

    const loading = loadingAssignment || loadingSubmissions || loadingStudents;

    const competencyLevels = [
        { code: 'EE', label: 'Exceeding Expectations', color: 'amber', bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-400/40' },
        { code: 'ME', label: 'Meeting Expectations', color: 'emerald', bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-400/40' },
        { code: 'AE', label: 'Approaching Expectations', color: 'blue', bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-400/40' },
        { code: 'BE', label: 'Below Expectations', color: 'rose', bg: 'bg-rose-50', text: 'text-rose-800', border: 'border-rose-400/40' }
    ];

    // Sync initial states when students list changes
    useEffect(() => {
        if (students.length > 0) {
            const initialGrades = {};
            const initialMarks = {};
            const initialComments = {};
            students.forEach(s => {
                const sub = submissions.find(sub => sub.student === s.id);
                initialGrades[s.id] = sub?.grade_data?.competency_level || '';
                initialMarks[s.id] = sub?.grade_data?.score || '';
                initialComments[s.id] = sub?.grade_data?.feedback || '';
            });
            setGrades(prev => ({ ...initialGrades, ...prev }));
            setRawMarks(prev => ({ ...initialMarks, ...prev }));
            setTeacherComments(prev => ({ ...initialComments, ...prev }));

            // Update currentIndex if initialStudentId is provided
            if (initialStudentId) {
                const index = students.findIndex(s => s.id === initialStudentId);
                if (index !== -1) setCurrentIndex(index);
            }
        }
    }, [students, submissions, initialStudentId]);

    // Auto-fill template based on assignment criteria (NEW)
    useEffect(() => {
        if (assignment && templates.length > 0 && !selectedTemplate) {
            const matchedTemplate = templates.find(t => 
                t.criteria_ee === assignment.criteria_ee &&
                t.criteria_me === assignment.criteria_me &&
                t.criteria_ae === assignment.criteria_ae &&
                t.criteria_be === assignment.criteria_be
            );
            if (matchedTemplate) {
                setSelectedTemplate(matchedTemplate);
            }
        }
    }, [assignment, templates, selectedTemplate]);

    const filteredStudents = useMemo(() => {
        return students.filter(student => {
            const hasSubmission = submissions.some(s => s.student === student.id);
            if (!hasSubmission) return false;

            const nameMatch = (student.name || student.full_name || `${student.first_name} ${student.last_name}`).toLowerCase();
            return nameMatch.includes(searchQuery.toLowerCase()) ||
                student.student_id?.toString().includes(searchQuery);
        });
    }, [students, submissions, searchQuery]);

    const currentStudent = filteredStudents[currentIndex];
    const currentSubmission = currentStudent ? submissions.find(s => s.student === currentStudent.id) : null;

    const evidenceUrl = currentSubmission?.file_url || currentSubmission?.file || currentSubmission?.evidence_url;

    const handleRawMarkChange = useCallback((studentId, mark) => {
        const numericMark = parseFloat(mark) || 0;
        const totalMarks = assignment?.total_marks || 100;
        const percentage = (numericMark / totalMarks) * 100;

        let level = 'BE';
        if (percentage >= 80) level = 'EE';
        else if (percentage >= 60) level = 'ME';
        else if (percentage >= 40) level = 'AE';

        setRawMarks(prev => ({ ...prev, [studentId]: mark }));
        setGrades(prev => ({ ...prev, [studentId]: level }));
    }, [assignment?.total_marks]);

    const handleLevelSelect = useCallback(async (studentId, level) => {
        if (submitting) return;

        const numericGrade = getScoreFromLevel(level, assignment?.total_marks);
        setGrades(prev => ({ ...prev, [studentId]: level }));
        setRawMarks(prev => ({ ...prev, [studentId]: numericGrade }));

        try {
            setSubmitting(true);
            const submission = submissions.find(s => s.student === studentId);
            const comment = teacherComments[studentId] || "Assessed via Evaluation Hub.";

            if (submission) {
                const endpoint = isClub 
                    ? `/api/events/club-submissions/${submission.id}/`
                    : `/api/assignment-submissions/${submission.id}/`;

                await patch(endpoint, {
                    status: 'graded',
                    competency_level: level,
                    competency_comment: comment,
                    grade: numericGrade,
                    score: numericGrade
                });

                queryClient.invalidateQueries(['assignment-students', assignmentId]);
                if (refresh) refresh();
            }

            showToast(`Evaluation recorded for ${(currentStudent.name || currentStudent.full_name).split(' ')[0]}`);

            if (currentIndex < filteredStudents.length - 1 && !isPage) {
                setTimeout(() => {
                    setCurrentIndex(prev => prev + 1);
                }, 300);
            } else if (!isPage) {
                showToast('All visible scholars assessed! 🎉');
            }
        } catch (err) {
            console.error('Error saving assessment:', err);
            showToast('Failed to save assessment', 'error');
        } finally {
            setSubmitting(false);
        }
    }, [assignment, submissions, currentIndex, filteredStudents, patch, showToast, currentStudent, teacherComments, submitting, assignmentId, isClub, queryClient, refresh, isPage]);

    if (loading) return (
        <div className="p-20 text-center bg-white rounded-[3rem]">
            <div className="w-12 h-12 border-4 border-[#18216D]/10 border-t-[#18216D] rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-[#18216D]/40">Initializing High-Velocity Hub...</p>
        </div>
    );

    // Template Creator View (Inside Hub)
    if (isForgingTemplate) {
        return (
            <BlueprintCreator
                learningAreas={[]} // Not needed when assignmentContext is provided
                assignmentContext={assignment}
                onCancel={() => setIsForgingTemplate(false)}
                onSuccess={(newTemplate) => {
                    queryClient.invalidateQueries(['learning-area-templates', laId]);
                    setSelectedTemplate(newTemplate);
                    setIsForgingTemplate(false);
                }}
            />
        );
    }

    return (
        <div className="w-full animate-in fade-in zoom-in-95 duration-500">
            {/* Unified Hub Header - Hidden in Page mode since Page has its own */}
            {!isPage && (
                <div className="bg-slate-50/50 px-10 py-8 border-b border-slate-100 relative overflow-hidden flex-shrink-0">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
                        <div className="flex-1 flex items-center gap-6">
                            <div className="w-14 h-14 bg-white rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center flex-shrink-0">
                                <AcademicCapIcon className="w-7 h-7 text-[#18216D]" />
                            </div>
                            <div className="min-w-0 pr-4">
                                <div className="flex flex-wrap items-center gap-3 mb-1">
                                    <h3 className="text-xl font-black text-[#18216D] tracking-tight uppercase leading-tight">{assignment?.title}</h3>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-[9px] font-black text-[#18216D]/60 uppercase tracking-widest">{assignment?.strand_name}</span>
                                    <span className="w-1 h-1 bg-slate-200 rounded-full" />
                                    <span className="text-[9px] font-black text-[#18216D]/60 uppercase tracking-widest">{assignment?.sub_strand_name}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="relative group min-w-[300px]">
                                <MagnifyingGlassIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D]" />
                                <input
                                    type="text"
                                    placeholder="Find scholar..."
                                    className="w-full pl-10 pr-4 py-3.5 bg-white border border-slate-100 rounded-2xl text-[10px] font-black text-[#18216D] focus:ring-4 focus:ring-[#18216D]/5 transition-all uppercase tracking-widest outline-none shadow-sm"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentIndex(0);
                                    }}
                                />
                            </div>
                            <button
                                onClick={onClose}
                                className="p-3.5 bg-white hover:bg-red-50 hover:text-red-500 border border-slate-100 rounded-2xl transition-all shadow-sm group"
                                title="Exit Hub"
                            >
                                <XMarkIcon className="w-6 h-6 text-slate-300 group-hover:text-red-500 transition-colors" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Template & Navigation Overlay (Mini) */}
            <div className={`px-10 py-5 bg-white border-b border-slate-50 flex items-center justify-between shadow-sm relative z-20 ${isPage ? 'rounded-t-[3rem]' : ''}`}>
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-3">
                        <select
                            value={selectedTemplate?.id || ''}
                            onChange={(e) => {
                                const t = templates.find(temp => temp.id === parseInt(e.target.value));
                                setSelectedTemplate(t || null);
                            }}
                            className="px-6 py-3 bg-slate-50 border border-slate-100 rounded-xl font-black text-[#18216D] text-[10px] tracking-widest uppercase cursor-pointer hover:bg-slate-100 transition-all outline-none"
                        >
                            <option value="">Custom Marking</option>
                            {templates.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>

                        <button
                            onClick={() => setIsForgingTemplate(true)}
                            className="flex items-center gap-2 px-5 py-3 bg-indigo-50 text-indigo-700 font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-indigo-100 transition-all border border-indigo-100/50 shadow-sm"
                        >
                            <PlusIcon className="w-3.5 h-3.5 stroke-[3]" />
                            Forge Rubric
                        </button>
                    </div>

                    <div className="w-px h-8 bg-slate-100 mx-2 hidden md:block" />

                    {/* Raw Mark Input System (NEW) */}
                    <div className="flex items-center gap-4 bg-slate-50 px-5 py-2.5 rounded-2xl border border-slate-100">
                        <span className="text-[10px] font-black text-[#18216D]/40 uppercase tracking-widest">Entry</span>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                value={rawMarks[currentStudent?.id] || ''}
                                onChange={(e) => handleRawMarkChange(currentStudent?.id, e.target.value)}
                                className="w-14 px-2 py-1 bg-white border border-slate-200 rounded-lg text-center font-black text-[#18216D] text-sm focus:ring-4 focus:ring-[#18216D]/10 outline-none transition-all"
                                placeholder="0"
                            />
                            <span className="text-sm font-black text-[#18216D]/20 italic">/</span>
                            <span className="text-sm font-black text-[#18216D] italic">{assignment?.total_marks}</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {!isPage && (
                        <div className="px-5 py-2 bg-[#18216D] rounded-full text-[10px] font-black text-white uppercase tracking-widest shadow-lg shadow-indigo-900/20">
                            {currentIndex + 1} / {filteredStudents.length} Scholars
                        </div>
                    )}
                    {isPage && (
                        <div className="px-5 py-2 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-100 flex items-center gap-2">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                            Live Evaluation
                        </div>
                    )}
                </div>
            </div>

            {/* Hub Content: Single Scholar Focus */}
            <div className="p-10 bg-[#18216D]/[0.02] flex items-center justify-center min-h-[500px]">
                {currentStudent ? (
                    <div className="w-full max-w-4xl bg-white rounded-[2.5rem] p-1 shadow-2xl shadow-indigo-900/10 border border-slate-100 animate-in slide-in-from-bottom-8 duration-500">
                        <div className="bg-white rounded-[2.2rem] p-8 space-y-10">

                            {/* Scholar Header / Bio */}
                            <div className="flex items-center justify-between bg-slate-50/50 p-6 rounded-3xl border border-slate-100/50">
                                <div className="flex items-center gap-6">
                                    <div className="w-16 h-16 bg-[#18216D] rounded-[1.2rem] flex items-center justify-center font-black text-white text-2xl shadow-lg shadow-indigo-900/10">
                                        {(currentStudent.name || currentStudent.full_name || currentStudent.first_name || 'S')[0].toUpperCase()}
                                    </div>
                                    <div>
                                        <h4 className="text-2xl font-black text-[#18216D] tracking-tighter italic">{currentStudent.name || currentStudent.full_name || `${currentStudent.first_name} ${currentStudent.last_name}`}</h4>
                                        <div className="flex items-center gap-4 mt-1.5 font-black text-[10px] text-slate-900 uppercase tracking-widest">
                                            <span>ID: {currentStudent.student_id || 'ST-005'}</span>
                                            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full" />
                                            <span>Submitted: {currentSubmission ? new Date(currentSubmission.submitted_at).toLocaleDateString() : 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    {evidenceUrl && (
                                        <a
                                            href={evidenceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-4 bg-white border border-slate-100 hover:bg-slate-900 hover:text-white rounded-2xl text-slate-900 transition-all shadow-md group"
                                            title="Download Evidence"
                                        >
                                            <DownloadIcon className="w-6 h-6" />
                                        </a>
                                    )}
                                    <button
                                        onClick={() => navigate(`/teacher/assignments/${assignment.id}/mark/${currentSubmission?.id}`)}
                                        className="p-4 bg-white border border-slate-100 hover:bg-emerald-600 hover:text-white rounded-2xl text-slate-900 transition-all shadow-md"
                                        title="Launch Marking Canvas"
                                    >
                                        <PencilSquareIcon className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                                {/* Left Side: Grading */}
                                <div className="space-y-6">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-[#18216D]">Achieved Competency</h4>
                                            <span className="text-[9px] font-black text-[#18216D] uppercase tracking-widest bg-white shadow-sm border border-slate-100 px-3 py-1.5 rounded-full italic">Fast-Mark Active</span>
                                        </div>
                                        <div className={`grid gap-6 ${isPage ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2' : 'grid-cols-2'}`}>
                                            {competencyLevels.map((level) => (
                                                <button
                                                    key={level.code}
                                                    type="button"
                                                    disabled={submitting}
                                                    onClick={() => handleLevelSelect(currentStudent.id, level.code)}
                                                    className={`p-8 rounded-[2.5rem] border-2 transition-all flex flex-col items-start gap-4 relative overflow-hidden group hover:scale-[1.01] active:scale-[0.98] ${grades[currentStudent.id] === level.code
                                                        ? `${level.border} ${level.bg} shadow-xl shadow-indigo-900/10`
                                                        : 'bg-white text-[#18216D]/30 border-slate-100 hover:border-[#18216D]/20 shadow-sm'
                                                        }`}
                                                >
                                                    <div className="flex items-center justify-between w-full">
                                                        <span className={`text-4xl font-black ${grades[currentStudent.id] === level.code ? level.text : 'text-slate-300'}`}>{level.code}</span>
                                                        {grades[currentStudent.id] === level.code && (
                                                            <CheckCircleIcon className={`w-8 h-8 ${level.text} animate-in zoom-in duration-300`} />
                                                        )}
                                                    </div>
                                                    <span className={`text-[11px] font-black uppercase tracking-[0.2em] ${grades[currentStudent.id] === level.code ? 'text-[#18216D]' : 'opacity-80'}`}>
                                                        {level.label}
                                                    </span>

                                                    {selectedTemplate && (
                                                        <p className={`text-[11px] text-[#18216D] font-bold italic leading-relaxed mt-2 pt-4 border-t border-slate-100 transition-all text-left ${
                                                            grades[currentStudent.id] === level.code ? 'opacity-100' : 'opacity-60'
                                                        }`}>
                                                            {selectedTemplate[`criteria_${level.code.toLowerCase()}`] || "No specific criteria defined."}
                                                        </p>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side: Personalized Feedback */}
                                <div className="space-y-6">
                                    <div className="space-y-4 h-full flex flex-col">
                                        <div className="flex items-center gap-3">
                                            <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-[#18216D]" />
                                            <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-[#18216D]">Scholar Feedback</h4>
                                        </div>
                                        <textarea
                                            value={teacherComments[currentStudent.id] || ''}
                                            onChange={(e) => setTeacherComments(prev => ({ ...prev, [currentStudent.id]: e.target.value }))}
                                            className="w-full flex-1 p-8 bg-slate-50 border-none rounded-[2.5rem] font-bold text-[#18216D] text-sm focus:ring-4 focus:ring-[#18216D]/5 transition-all shadow-inner placeholder:text-slate-400 placeholder:italic min-h-[300px]"
                                            placeholder="Add personalized feedback for the report card..."
                                        />
                                        <div className="mt-4 flex items-center gap-3 justify-center text-[10px] font-black text-[#18216D] uppercase tracking-widest bg-[#18216D]/5 py-3.5 rounded-2xl border border-[#18216D]/10 px-6">
                                            <CheckCircleIcon className="w-4 h-4 text-emerald-600" />
                                            Marking Autosaves Progress
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Navigation Footer - Only show in Modal mode */}
                            {!isPage && (
                                <div className="flex items-center justify-between pt-8 border-t border-slate-100">
                                    <button
                                        onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
                                        disabled={currentIndex === 0}
                                        className="flex items-center gap-4 text-[11px] font-black text-slate-500 uppercase tracking-widest hover:text-[#18216D] disabled:opacity-0 transition-all group"
                                    >
                                        <div className="p-3 bg-slate-100 rounded-2xl group-hover:bg-[#18216D] group-hover:text-white transition-all shadow-sm">
                                            <ChevronLeftIcon className="w-5 h-5" />
                                        </div>
                                        Previous Scholar
                                    </button>
                                    <button
                                        onClick={() => setCurrentIndex(prev => Math.min(filteredStudents.length - 1, prev + 1))}
                                        disabled={currentIndex === filteredStudents.length - 1}
                                        className="flex items-center gap-4 text-[11px] font-black text-slate-500 uppercase tracking-widest hover:text-[#18216D] disabled:opacity-0 transition-all group"
                                    >
                                        Skip Scholar
                                        <div className="p-3 bg-slate-100 rounded-2xl group-hover:bg-[#18216D] group-hover:text-white transition-all shadow-sm">
                                            <ChevronRightIcon className="w-5 h-5" />
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-24 animate-in fade-in zoom-in duration-700">
                        <div className="w-28 h-28 bg-white rounded-[3rem] shadow-2xl shadow-indigo-900/10 flex items-center justify-center mx-auto mb-10 border border-slate-50 relative">
                            <AcademicCapIcon className="w-12 h-12 text-[#18216D]" />
                            <div className="absolute inset-0 bg-[#18216D]/5 rounded-[3rem] animate-ping" />
                        </div>
                        <h4 className="text-3xl font-black text-[#18216D] tracking-tighter uppercase italic">
                            Evaluations Finalized
                        </h4>
                        <p className="text-slate-500 font-black uppercase tracking-[0.2em] text-[11px] mt-4 flex items-center justify-center gap-3">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            All scholars successfully assessed
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GradeForm;
